import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { BreachedCase } from '@compass/client/types/cases_analytics';
import React, { useEffect, useMemo, useState } from 'react';

import { GenericTable } from '@/components/GenericTable';
import { type CasesTableContext, createCasesTableConfig } from '@/components/TableBuilder';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import type { UseAssigneeOptionsReturn } from '@/hooks/useAssigneeOptions';
import { useOrgPqcWorkflows } from '@/hooks/useOrgPqcWorkflows';
import { buildRoutePath } from '@/utils/routes';

import { useBreachedCaseHydration } from '../hooks/useBreachedCaseHydration';

interface BreachedCasesSectionProps {
  organizationId: string;
  /** The response's breached_case_list: created_at DESC, capped server-side. */
  rows: BreachedCase[];
  /** summary.breached_cases.value (same filters, same store). */
  totalBreached: number;
  /** Changes whenever the page filters change; resets the table to its first page. */
  filterKey: string;
  /** The page-level loading gate. */
  loading?: boolean;
}

/** No assignees to offer, so the Assignee cell stays read-only: the page has no reassign. */
const NO_ASSIGNEE_OPTIONS: UseAssigneeOptionsReturn = {
  groups: [],
  allUserIds: [],
  usersMap: new Map(),
  isLoading: false,
};

/** Of the org-wide PQC cases columns, keep Case ID, Assignee, Manufacturer, Created at and Completed at. */
const EXCLUDED_COLUMNS = ['created_by', 'state'];

/** A row the hydration missed, from its ClickHouse fields. */
const toCaseRecord = (organizationId: string, row: BreachedCase): GetCaseRecordResponse => ({
  id: row.case_record_id,
  organization_id: organizationId,
  program_id: row.program_id,
  workflow_id: row.workflow_id,
  created_at: row.created_at,
  assigned_user_id: row.assigned_user_id,
  // AssigneeSelect reads an empty `assigned` as Unassigned; it resolves the name by id.
  assigned: row.assigned_user_id,
});

/**
 * The SLA breached cases table at the bottom of the AE/PQC Analytics page, drawn with the
 * org-wide PQC cases columns. The rows come from ClickHouse (ids, created-at, assignee/program/
 * workflow ids); each visible page is hydrated through the case API for the code and completed
 * time, and the manufacturer is the PQC workflow's name. A row the hydration missed still
 * renders from its ClickHouse fields and stays clickable.
 */
export const BreachedCasesSection = React.forwardRef<HTMLElement, BreachedCasesSectionProps>(
  ({ organizationId, rows, totalBreached, filterKey, loading }, ref) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    useEffect(() => {
      setPage(1);
    }, [filterKey]);

    const pageRows = useMemo(
      () => rows.slice((page - 1) * pageSize, page * pageSize),
      [rows, page, pageSize],
    );
    const { recordById, isLoading: hydrationLoading } = useBreachedCaseHydration(
      organizationId,
      pageRows,
    );
    const records = useMemo(
      () =>
        pageRows.map(
          row => recordById.get(row.case_record_id ?? '') ?? toCaseRecord(organizationId, row),
        ),
      [pageRows, recordById, organizationId],
    );

    const { pqcWorkflows } = useOrgPqcWorkflows();
    const tableContext: CasesTableContext = useMemo(() => {
      const workflowNameById = new Map<string, string>();
      for (const w of pqcWorkflows) {
        if (w.id) workflowNameById.set(w.id, w.name || w.id);
      }
      return { workflowNameById, pqcView: true, assigneeOptions: NO_ASSIGNEE_OPTIONS };
    }, [pqcWorkflows]);

    const tableConfig = useMemo(
      () => ({
        ...createCasesTableConfig(
          null,
          undefined,
          undefined,
          undefined,
          item => buildRoutePath(`/cases/${item.id}`, organizationId, item.program_id ?? ''),
          undefined,
          undefined,
          undefined,
          EXCLUDED_COLUMNS,
          undefined,
          { includeColumns: ['manufacturer'], context: tableContext },
        ),
        emptyState: {
          title: 'No SLA breached cases',
          description: 'No SLA breached cases in this period.',
        },
      }),
      [organizationId, tableContext],
    );

    return (
      <section ref={ref} className="space-y-3 scroll-mt-4">
        <header className="space-y-1">
          <h3 className="text-sm font-semibold leading-5 text-foreground">SLA Breached Cases</h3>
          <p className="text-xs leading-5 text-muted-foreground">
            Submitted cases that exceeded the 24-business-hour SLA, newest first.
            {rows.length < totalBreached &&
              ` Showing the newest ${rows.length} of ${totalBreached}; narrow the date range to see the rest.`}
          </p>
        </header>
        <GenericTable<GetCaseRecordResponse>
          config={tableConfig}
          data={records}
          loading={loading || hydrationLoading}
          currentPage={page}
          hasNextPage={page * pageSize < rows.length}
          onPageChange={setPage}
          pageSize={pageSize}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </section>
    );
  },
);

BreachedCasesSection.displayName = 'BreachedCasesSection';
